import { orderBy } from 'lodash-es';
import React, { useMemo, useState } from 'react';

import { Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2'; // Grid version 2
import { useSeekaConverge } from '@seeka-labs/converge-react';
import {
    useContentfulInspectorMode, useContentfulLiveUpdates
} from '../../shared/hooks/contentful';

import ClientOnlyReactPlayer from '../../components/reactPlayer';
import { Button } from '../../components/ui';
import { ExternalReferenceInfoItem, ExternalReferenceInfoItemType } from '../../pages/learn';
import { renderContentfulRichText } from '../../shared/utils';

// const ClientOnlyReactPlayer = React.lazy(() =>
//     import("../../components/reactPlayer")
// )

interface InfoTagsProps {
    tags: string[]
    onTagSelect: (tag: string) => void
}

export const getColorForExternalReferenceInfoTag = (tag: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (tag === 'Safety' || tag === 'Side effects' || tag === 'Risks' || tag === 'Dosage') return 'warning';

    return 'default';
}

const InfoTags = ({ tags, onTagSelect, ...other }: InfoTagsProps) => {

    return <Stack direction='row' gap={1} useFlexGap flexWrap="wrap" {...other}>
        {
            tags.map((t) => {
                const color = getColorForExternalReferenceInfoTag(t);

                return <Chip key={t} label={t} size='small' onClick={() => onTagSelect(t)} color={color} variant="outlined" />
            })
        }
    </Stack>
}


interface ExternalReferenceInfoItemCardProps extends ExternalReferenceInfoItem {
    onTagSelect: (tag: string) => void
}

export function ExternalReferenceInfoItemCard({
    onTagSelect,
    ...props
}: ExternalReferenceInfoItemCardProps) {
    const inspectorProps = useContentfulInspectorMode();
    const data = useContentfulLiveUpdates({
        ...props,
        sys: { id: props },
    });
    const [isVideoPlaying, setIsVideoPlaying] = useState(false)
    const converge = useSeekaConverge();

    const tags = useMemo(() => {
        return orderBy(data.tags, (t) => t, 'asc')
    }, [data.tags])


    const isSSR = typeof window === "undefined"

    const imageDimensions = {
        xs: "70px",
        sm: "70px",
        md: "70px",
        lg: "100px",
        xl: "100px",
    }

    const isVideo = data.referenceType === ExternalReferenceInfoItemType.Video;

    const onClickPreview = () => {
        setIsVideoPlaying(true)
        onClickItem()
    }

    const onClickItem = () => {
        converge.track.viewContentItem({
            categoryName: 'topic',
            items: props.tags.map((t) => ({
                categoryName: 'topic.implicit',
                productIdentifier: t,
                productName: t
            }))
        })

        const clar = (window as any).clarity;
        if (clar) {
            clar("set", "topic.implicit", props.tags)
        }

        (window as any).klaviyo?.push(['track', 'topic.implicit', {
            'topics': props.tags
        }]);
    }

    // const logo = useMemo(() => {
    //     return  <ContentfulAssetImage
    //     asset={data.logo}
    //     contentfulPreviewProps={inspectorProps({
    //         entryId: props.contentful_id,
    //         fieldId: 'logo',
    //     })}
    //     imgStyle={
    //         !data.logo || data.logo.gatsbyImageData
    //             ? undefined
    //             : { width: "100%" }
    //     }
    // />
    // }, [data.logo])

    const card = (
        <Card sx={{ height: "100%" }} variant="outlined">
            <CardContent sx={{ pb: 1, pt: 1 }}>
                <Stack direction={"column"} gap={1}>
                    {/* <Stack direction={"row"} gap={4} alignItems={"center"} sx={{ pb: 1 }}> */}
                    {/* <Stack
                            sx={{ height: imageDimensions }}
                            direction="column"
                            justifyContent="center"
                        >
                            <div>
                                {
                                    logo
                                }
                            </div>
                        </Stack> */}
                    <Typography
                        variant="body1"
                        sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#160637" }}
                        component="div"
                        {...inspectorProps({
                            entryId: props.contentful_id,
                            fieldId: 'sourceName',
                        })}
                    >
                        {data.sourceName}
                    </Typography>
                    {/* </Stack> */}

                    {/* <Divider /> */}

                    {tags && (
                        <InfoTags tags={tags} {...inspectorProps({
                            entryId: props.contentful_id,
                            fieldId: 'tags',
                        })} onTagSelect={onTagSelect} />)}

                    {/* <Divider /> */}

                    {data.excerpt && (
                        <Typography
                            variant="body2"
                            component={"div"}
                            color="text.secondary"
                            {...inspectorProps({
                                entryId: props.contentful_id,
                                fieldId: 'excerpt',
                            })}
                        >
                            {renderContentfulRichText(data.excerpt)}
                        </Typography>
                    )}

                    {isVideo && !isSSR && (
                        <>
                            {/* <Box onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}> */}
                            <React.Suspense fallback={<div />}>
                                <ClientOnlyReactPlayer light url={data.externalLink} onClickPreview={onClickPreview} playing={isVideoPlaying} width={'100%'} />
                            </React.Suspense>


                            {/* </Box> */}

                            {/* <FullScreenDialog open={isDialogOpen} handleClose={() => setIsDialogOpen(false)} title={data.sourceName}>
                                <ReactPlayer url={data.externalLink} playing={true} width={'100%'} />
                            </FullScreenDialog> */}
                        </>

                    )}

                    {!isVideo ? <Button href={data.externalLink} onClick={onClickItem} size='small' variant='outlined'>Learn more</Button> : <></>}
                </Stack>
            </CardContent>
        </Card>
    )

    return (
        (<Grid
            alignItems={"stretch"}
            size={{
                xs: 12,
                sm: 12,
                md: 12,
                lg: 6,
                xl: 4
            }}>
            {card}
        </Grid>)
    );
}